import * as React from 'react';
import { cn } from '../../utils/cn';
import { Box, BoxProps } from './Box';

export interface FlexProps extends Omit<BoxProps, 'display'> {
  direction?: 'row' | 'row-reverse' | 'col' | 'col-reverse';
  wrap?: 'wrap' | 'wrap-reverse' | 'nowrap';
  align?: 'start' | 'end' | 'center' | 'baseline' | 'stretch';
  justify?: 'start' | 'end' | 'center' | 'between' | 'around' | 'evenly';
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16;
  inline?: boolean;
}

const getDirectionClass = (direction: FlexProps['direction']): string => {
  switch (direction) {
    case 'row':
      return 'flex-row';
    case 'row-reverse':
      return 'flex-row-reverse';
    case 'col':
      return 'flex-col';
    case 'col-reverse':
      return 'flex-col-reverse';
    default:
      return 'flex-row';
  }
};

const getWrapClass = (wrap: FlexProps['wrap']): string => {
  switch (wrap) {
    case 'wrap':
      return 'flex-wrap';
    case 'wrap-reverse':
      return 'flex-wrap-reverse';
    case 'nowrap':
      return 'flex-nowrap';
    default:
      return 'flex-nowrap';
  }
};

const getAlignClass = (align: FlexProps['align']): string => {
  switch (align) {
    case 'start':
      return 'items-start';
    case 'end':
      return 'items-end';
    case 'center':
      return 'items-center';
    case 'baseline':
      return 'items-baseline';
    case 'stretch':
      return 'items-stretch';
    default:
      return 'items-stretch';
  }
};

const getJustifyClass = (justify: FlexProps['justify']): string => {
  switch (justify) {
    case 'start':
      return 'justify-start';
    case 'end':
      return 'justify-end';
    case 'center':
      return 'justify-center';
    case 'between':
      return 'justify-between';
    case 'around':
      return 'justify-around';
    case 'evenly':
      return 'justify-evenly';
    default:
      return 'justify-start';
  }
};

export const Flex = React.forwardRef<HTMLElement, FlexProps>(
  (
    {
      direction = 'row',
      wrap = 'nowrap',
      align = 'stretch',
      justify = 'start',
      gap,
      inline = false,
      className,
      ...props
    },
    ref
  ) => {
    const flexClasses = cn(
      getDirectionClass(direction),
      getWrapClass(wrap),
      getAlignClass(align),
      getJustifyClass(justify),
      gap !== undefined && `gap-${gap}`,
      className
    );

    return (
      <Box ref={ref} display={inline ? 'inline-flex' : 'flex'} className={flexClasses} {...props} />
    );
  }
);

Flex.displayName = 'Flex';
